import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";

interface MenuPosition {
  left: number;
  top: number;
  maxHeight: number;
}

interface ViewportSize {
  width: number;
  height: number;
}

interface MenuSize {
  width: number;
  height: number;
}

type AnchorRect = Pick<DOMRect, "left" | "right" | "top" | "bottom">;

export function calculateActionMenuPosition(anchor: AnchorRect, menu: MenuSize, viewport: ViewportSize): MenuPosition {
  const margin = 8;
  const gap = 6;
  const availableWidth = Math.max(0, viewport.width - margin * 2);
  const width = Math.min(menu.width, availableWidth);
  const left = Math.min(Math.max(margin, anchor.right - width), Math.max(margin, viewport.width - width - margin));
  const spaceBelow = viewport.height - anchor.bottom - margin;
  const spaceAbove = anchor.top - margin;
  const openAbove = spaceBelow < menu.height + gap && spaceAbove > spaceBelow;
  const idealTop = openAbove ? anchor.top - menu.height - gap : anchor.bottom + gap;
  const top = Math.min(Math.max(margin, idealTop), Math.max(margin, viewport.height - Math.min(menu.height, viewport.height - margin * 2) - margin));
  return { left, top, maxHeight: Math.max(80, Math.min(viewport.height - margin * 2, openAbove ? spaceAbove - gap : spaceBelow - gap)) };
}

export function ActionMenu({ children, label = "Actions", className = "" }: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  function focusableItems() {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>(
      "button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href]",
    ) || []);
  }

  function close(returnFocus = false) {
    setOpen(false);
    setPosition(null);
    if (returnFocus) queueMicrotask(() => triggerRef.current?.focus());
  }

  function updatePosition() {
    if (!triggerRef.current || !menuRef.current) return;
    const anchor = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    setPosition(calculateActionMenuPosition(anchor, menu, { width: window.innerWidth, height: window.innerHeight }));
  }

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) close();
    };
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    const onViewportChange = () => updatePosition();
    document.addEventListener("pointerdown", onOutside);
    document.addEventListener("keydown", onEscape);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", onOutside);
      document.removeEventListener("keydown", onEscape);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [open]);

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    setOpen(true);
    queueMicrotask(() => {
      const items = focusableItems();
      (event.key === "ArrowUp" ? items.at(-1) : items[0])?.focus();
    });
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const items = focusableItems();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const offset = event.key === "ArrowDown" ? 1 : -1;
    const next = current < 0 ? 0 : (current + offset + items.length) % items.length;
    event.preventDefault();
    items[next]?.focus();
  }

  const menu = <div id={menuId} ref={menuRef} className="action-menu-popover" role="menu" hidden={!open}
    style={position ? { left: position.left, top: position.top, maxHeight: position.maxHeight, visibility: "visible" } : { visibility: "hidden" }}
    onKeyDown={onMenuKeyDown}
    onClick={(event) => {
      const button = (event.target as HTMLElement).closest("button:not([disabled])");
      if (button) close();
    }}>
    {children}
  </div>;

  return <div className={`action-menu ${className}`.trim()}>
    <button ref={triggerRef} type="button" className="action-menu-trigger" aria-haspopup="menu"
      aria-expanded={open} aria-controls={menuId} onKeyDown={onTriggerKeyDown} onClick={() => setOpen((current) => !current)}>
      <span>{label}</span><span className="action-menu-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && typeof document !== "undefined" ? createPortal(menu, document.body) : menu}
  </div>;
}

export function ActionMenuItem({ className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} type={props.type || "button"} role="menuitem" className={`action-menu-item ${className}`.trim()} />;
}

export function ActionMenuDivider() {
  return <div className="action-menu-divider" role="separator" />;
}
